// RolePermissionsPage.jsx
import { useState, useEffect, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useGetCompanyRolesQuery, useUpdateRolePermissionsMutation } from "./api/companyRolesApiSlice";
import PageLayout from "components/layout/PageLayout";
import SuccessMessage from "components/commons/SuccessMessage";
import { NavLink } from "react-router-dom";


const RolePermissionsPage = () => {
    const [t] = useTranslation(["global", "absenceReport","whistle", "health", "safety", "device"])

    const {
        data: rolesWithPermissions = [],
        isFetching,
        isSuccess,
        refetch
    } = useGetCompanyRolesQuery();

    const [updatePermissions, { isSuccess: saveSuccess }] = useUpdateRolePermissionsMutation();
    const [localState, setLocalState] = useState([]);
    const [dirtyRoles, setDirtyRoles] = useState(new Set());

    useEffect(() => {
        if (isSuccess) {
            setLocalState(rolesWithPermissions);
            setDirtyRoles(new Set());
        }
    }, [isSuccess, rolesWithPermissions]);

    const handleCheckboxChange = (roleGuid, permKey, checked) => {
        const updated = localState.map((role) => {
            if (role.role.guid !== roleGuid) return role;
            const updatedPerms = role.permissions.map((perm) =>
                perm.key === permKey ? { ...perm, value: checked } : perm
            );
            return { ...role, permissions: updatedPerms };
        });
        setLocalState(updated);
        setDirtyRoles((prev) => new Set(prev).add(roleGuid));
    };

    const handleSave = async () => {
        for (const role of localState.filter((r) => dirtyRoles.has(r.role.guid))) {
            const grantedPermissions = role.permissions.filter(p => p.value).map(p => p.key);
            await updatePermissions({
                guid: role.role.guid,
                permissions: grantedPermissions,
                isCritical: role.role.isCritical
            });
        }
        // refetch();
    };


    const renderPermissionLabel = (permission, module) => {
        switch (module) {
            case "CORE": return t(`permission.${permission}`)
            case "HR": return t(`absenceReport:permission.${permission}`)
            case "WHISTLE": return t(`whistle:permission.${permission}`)
            case "HEALTH": return t(`health:permission.${permission}`)
            case "SAFETY": return t(`safety:permission.${permission}`)
            case "DEVICES": return t(`device:permission.${permission}`)
            default: return permission
        }


    }

    return (
        <PageLayout
            pageTitle={t("entity.roles")}
            isLoadingContent={isFetching}
            onPageRefresh={refetch}
            bodyContent={
                <div className="overflow-auto">
                    {saveSuccess && <SuccessMessage />}
                    <table className="table table-dark table-bordered text-center align-middle">
                        <thead>
                            <tr>
                                <th></th>
                                {localState.map((r) => (
                                    <th key={r.role.guid} className="text-uppercase p-2">
                                        <div
                                            className="dropdown"
                                            style={{
                                                backgroundColor: r.role.backgroundColor,
                                                color: r.role.textColor,
                                                padding: "0.25rem",
                                                borderRadius: "0.25rem",
                                            }}
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-sm dropdown-toggle fw-bold w-100"
                                                style={{ backgroundColor: r.role.backgroundColor, color: r.role.textColor }}
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                            >
                                                {r.role.name}
                                            </button>
                                            <ul className="dropdown-menu">
                                                <NavLink
                                                    to={{ pathname: "/dashboard/roles/edit" }}
                                                    state={{ roleGUID: r.role.guid }}
                                                    className="dropdown-item"
                                                >
                                                    <i className="fa-solid fa-pen"></i> {t("action.edit")}
                                                </NavLink>
                                                <NavLink
                                                    to={{ pathname: "/dashboard/roles/edit" }}
                                                    state={{ roleGUID: r.role.guid }}
                                                    className="dropdown-item disabled"
                                                >
                                                    <i className="fa-solid fa-trash"></i> {t("action.delete")}
                                                </NavLink>
                                            </ul>

                                        </div>
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                <th>{t("entity.critical_role")}</th>
                                {localState.map((r) => (
                                    <td key={r.role.guid} className="text-center p-2 ">

                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={r.role.isCritical}
                                            onChange={(e) => {
                                                const updated = localState.map(role =>
                                                    role.role.guid === r.role.guid
                                                        ? { ...role, role: { ...role.role, isCritical: e.target.checked } }
                                                        : role
                                                );
                                                setLocalState(updated);
                                                setDirtyRoles((prev) => new Set(prev).add(r.role.guid));
                                            }}
                                        />
                                    </td>

                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {[...new Set(localState.flatMap(r => r.permissions.map(p => p.module)))].map(module => (
                                <Fragment key={module}>
                                    <tr className="table-secondary">
                                        <td colSpan={localState.length + 1} className="text-start fw-bold">{module}</td>
                                    </tr>
                                    {Array.from(new Set(localState.flatMap(r => r.permissions.filter(p => p.module === module).map(p => p.key)))).map(perm => (
                                        <tr key={module + perm}>
                                            <td className="text-start">{renderPermissionLabel(perm, module)}</td>
                                            {localState.map(r => {
                                                const p = r.permissions.find(p => p.key === perm);
                                                return (
                                                    <td key={r.role.guid + perm}>
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={p?.value || false}
                                                            onChange={(e) => handleCheckboxChange(r.role.guid, perm, e.target.checked)}
                                                        />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                    {dirtyRoles.size > 0 && (
                        <button className="btn mb-3 btn-primary btn-lg btn-block" onClick={handleSave}><i className="fa-solid fa-floppy-disk"></i> {t("action.save")}</button>
                    )}
                </div>
            }
        />
    );
};

export default RolePermissionsPage;
